import { useState } from 'react'
import moment from 'moment-timezone'
import { Clock } from 'iconoir-react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Label } from 'app/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { isSchedulable, schedulePresets } from 'app/lib/scheduledSend'

export type SendLaterButtonProps = {
  /** The practice IANA zone. Every offered time is on this clock. */
  timezone?: string
  disabled?: boolean
  /** When set, the control renders nothing at all. A disabled clock the user
   *  cannot explain is worse than no clock. */
  unavailableReason?: string
  /**
   * How the trigger presents. `composer` is the clock beside Send, sized to sit
   * with the other composer controls. `inline` is for a row of text actions, as
   * in the tray, where the composer's filled 44px square outweighs everything
   * beside it and makes rescheduling look like the main thing on offer.
   */
  appearance?: `composer` | `inline`
  onSchedule: (sendAtLocal: string) => void
}

/**
 * The clock beside Send. Offers only times the practice can actually send at:
 * presets adapt to the current practice hour, and the custom picker refuses
 * anything inside quiet hours rather than accepting it and failing later.
 */
export function SendLaterButton({
  timezone,
  disabled,
  unavailableReason,
  appearance = `composer`,
  onSchedule,
}: SendLaterButtonProps) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState(``)
  const [time, setTime] = useState(``)
  const [error, setError] = useState(``)

  // Presence is not validity: moment-timezone resolves an unrecognized zone
  // name as UTC with `isValid()` still true, so a malformed practice timezone
  // would compute every offered time on UTC and hand an Eastern practice a
  // 3:00 AM send labelled "Tomorrow 8:00 AM". Refused the same way
  // `scheduleUnavailableReason` refuses it, by rendering nothing at all.
  if (unavailableReason || !timezone || !moment.tz.zone(timezone)) return null

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      // Clear any state left over from an abandoned attempt (a quiet-hours
      // refusal the user walked away from, or a half-filled date/time) so
      // reopening never shows a stale error for a session that's already
      // over. This runs on every close, not only a successful pick: Escape,
      // an outside click, and toggling the trigger shut all report through
      // `onOpenChange`, and `choose` below routes its own close through here
      // too rather than calling `setOpen` directly, so there is exactly one
      // place transient state gets cleared.
      setDate(``)
      setTime(``)
      setError(``)
    }
  }

  const choose = (sendAtLocal: string) => {
    handleOpenChange(false)
    onSchedule(sendAtLocal)
  }

  const submitCustom = () => {
    if (!date || !time) {
      setError(`Pick a date and a time`)
      return
    }
    const sendAtLocal = `${date}T${time}`
    if (!isSchedulable(sendAtLocal, timezone)) {
      setError(`Pick a future time between 8:00 AM and 8:00 PM at the practice`)
      return
    }
    choose(sendAtLocal)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {appearance === `inline` ? (
          <Button type="button" variant="subtle" size="sm" disabled={disabled} aria-label="Change the send time">
            <Clock className="mr-1 h-3.5 w-3.5" strokeWidth={2} />
            Change time
          </Button>
        ) : (
          <Button
            type="button"
            variant="iconFilled"
            size="icon"
            disabled={disabled}
            title="Schedule this message"
            aria-label="Schedule this message"
            className="h-11 w-11"
          >
            <Clock className="h-6 w-6" strokeWidth={2} />
          </Button>
        )}
      </PopoverTrigger>
      {/* Radix marks the body pointer-events:none inside modal dialogs, which
          would make this portaled panel unclickable in the bulk modal. */}
      <PopoverContent align="end" className="pointer-events-auto w-72 p-2">
        <div className="flex flex-col gap-1">
          {schedulePresets(timezone).map((preset) => (
            <Button key={preset.id} type="button" variant="ghost" size="row" onClick={() => choose(preset.sendAtLocal)}>
              {preset.label}
            </Button>
          ))}
        </div>
        <div className="mt-2 flex flex-col gap-1.5 border-t border-trn-200 pt-2">
          <Label className="text-xs" htmlFor="send-later-date">
            Date
          </Label>
          <Input
            id="send-later-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-8"
          />
          <Label className="text-xs" htmlFor="send-later-time">
            Time
          </Label>
          <Input
            id="send-later-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-8"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <Button type="button" size="md" onClick={submitCustom}>
            Schedule
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
